/**
 * Commenting a line or a selection with Ctrl+/.
 *
 * A line is commented when its first code is `--`; a `--` that starts later
 * on the line is a note about code, and does not make the line commented.
 * The toggle decides by the lines the selection covers, first to last, so a
 * selection that ends mid-line still treats whole lines. When every covered
 * line is commented, the marks come off; otherwise every line without a mark
 * gets one in front of its code, which leaves the indentation alone.
 */

import { EditorView } from '@codemirror/view';

/** Whether a line's own code is commented. */
export function isCommented(line: string): boolean {
  return /^[ \t]*--/.test(line);
}

/** The lines a selection covers, each once, in document order. */
export function selectedLines(view: EditorView): number[] {
  const { state } = view;
  const numbers = new Set<number>();
  for (const range of state.selection.ranges) {
    const first = state.doc.lineAt(range.from).number;
    const last = state.doc.lineAt(range.to).number;
    for (let number = first; number <= last; number += 1) {
      numbers.add(number);
    }
  }
  return [...numbers].sort((left, right) => left - right);
}

/** Toggles `--` over the lines the selection covers. Returns whether it ran. */
export function toggleComments(view: EditorView): boolean {
  const { state } = view;
  if (!state.facet(EditorView.editable)) {
    return false;
  }
  const numbers = selectedLines(view);
  if (numbers.length === 0) {
    return false;
  }
  const takeOff = numbers.every((number) => isCommented(state.doc.line(number).text));
  const changes = numbers.flatMap((number) => {
    const line = state.doc.line(number);
    if (takeOff) {
      const mark = /^([ \t]*)-- ?/.exec(line.text);
      if (mark === null) {
        return [];
      }
      const indent = mark[1] ?? '';
      return [{ from: line.from + indent.length, to: line.from + mark[0].length, insert: '' }];
    }
    if (isCommented(line.text)) {
      return [];
    }
    const indent = /^[ \t]*/.exec(line.text)?.[0] ?? '';
    const empty = line.text.slice(indent.length) === '';
    const insert = empty ? '--' : '-- ';
    const at = line.from + indent.length;
    return [{ from: at, to: at, insert }];
  });
  if (changes.length === 0) {
    return false;
  }
  view.dispatch({ changes });
  return true;
}
